import { useState, useCallback, useRef, useEffect } from 'react';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { TrendingUp, ChevronRight, ChevronDown } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import {
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
} from '@/components/ui/sidebar';
import {
  getStoredExpansionState,
  toggleExpansionState,
} from '@/lib/investmentMenuState';
import { SecuritiesMenuItem } from '@/components/securities-menu-item/SecuritiesMenuItem';

interface InvestmentSubMenuItem {
  title: string;
  url: string;
  slug: string;
}

const investmentSubItems: InvestmentSubMenuItem[] = [
  { title: 'Overview', url: '/investments/overview', slug: 'overview' },
  { title: 'Money Market Funds', url: '/investments/money-market-funds', slug: 'money-market-funds' },
  { title: 'Fixed Income Funds', url: '/investments/fixed-income-funds', slug: 'fixed-income-funds' },
  { title: 'Balanced Funds', url: '/investments/balanced-funds', slug: 'balanced-funds' },
  { title: 'Mutual Funds', url: '/investments/mutual-funds', slug: 'mutual-funds' },
  { title: 'Special Funds', url: '/investments/special-funds', slug: 'special-funds' },
  { title: 'Pension Plans', url: '/investments/pension-plans', slug: 'pension-plans' },
  { title: 'REITs', url: '/investments/reits', slug: 'reits' },
  { title: 'Sacco', url: '/investments/sacco', slug: 'sacco' },
  { title: 'Crypto', url: '/investments/crypto', slug: 'crypto' },
];

interface InvestmentMenuItemProps {
  collapsed: boolean;
}

export const InvestmentMenuItem: React.FC<InvestmentMenuItemProps> = ({ collapsed }) => {
  const [expanded, setExpanded] = useState<boolean>(() => getStoredExpansionState());
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const location = useLocation();
  const isInvestmentsActive = location.pathname.startsWith('/investments');
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const subMenuItemsRef = useRef<(HTMLAnchorElement | null)[]>([]);

  const handleToggleExpanded = useCallback(() => {
    const newExpanded = !expanded;
    setExpanded(newExpanded);
    toggleExpansionState(expanded);
    
    // Focus management: when expanding, focus first submenu item
    if (newExpanded && !collapsed) {
      setTimeout(() => {
        if (subMenuItemsRef.current[0]) {
          subMenuItemsRef.current[0]?.focus();
          setFocusedIndex(0);
        }
      }, 10);
    }
    // When collapsing, focus the menu button
    else if (!newExpanded) {
      setTimeout(() => {
        menuButtonRef.current?.focus();
        setFocusedIndex(-1);
      }, 10);
    }
  }, [expanded, collapsed]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      switch (event.key) {
        case 'Enter':
        case ' ':
          event.preventDefault();
          handleToggleExpanded();
          break;
        case 'ArrowDown':
          if (expanded && !collapsed) {
            event.preventDefault();
            if (subMenuItemsRef.current[0]) {
              subMenuItemsRef.current[0]?.focus();
              setFocusedIndex(0);
            }
          }
          break;
        case 'Escape':
          if (expanded) {
            event.preventDefault();
            setExpanded(false);
            toggleExpansionState(true);
            menuButtonRef.current?.focus();
            setFocusedIndex(-1);
          }
          break;
      }
    },
    [expanded, collapsed, handleToggleExpanded]
  );

  const handleSubMenuItemKeyDown = useCallback(
    (index: number, event: React.KeyboardEvent<HTMLAnchorElement>) => {
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          const nextIndex = (index + 1) % investmentSubItems.length;
          subMenuItemsRef.current[nextIndex]?.focus();
          setFocusedIndex(nextIndex);
          break;
        case 'ArrowUp':
          event.preventDefault();
          const prevIndex = index === 0 ? investmentSubItems.length - 1 : index - 1;
          subMenuItemsRef.current[prevIndex]?.focus();
          setFocusedIndex(prevIndex);
          break;
        case 'Home':
          event.preventDefault();
          subMenuItemsRef.current[0]?.focus();
          setFocusedIndex(0);
          break;
        case 'End':
          event.preventDefault();
          subMenuItemsRef.current[investmentSubItems.length - 1]?.focus();
          setFocusedIndex(investmentSubItems.length - 1);
          break;
        case 'Escape':
          event.preventDefault();
          setExpanded(false);
          toggleExpansionState(true);
          menuButtonRef.current?.focus();
          setFocusedIndex(-1);
          break;
      }
    },
    []
  );

  // Reset focused index when menu collapses
  useEffect(() => {
    if (!expanded || collapsed) {
      setFocusedIndex(-1);
    }
  }, [expanded, collapsed]);

  // Update submenu item refs when expanded state changes
  useEffect(() => {
    if (!expanded || collapsed) {
      subMenuItemsRef.current = [];
    }
  }, [expanded, collapsed]);

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        ref={menuButtonRef}
        onClick={handleToggleExpanded}
        onKeyDown={handleKeyDown}
        isActive={isInvestmentsActive}
        className="hover:bg-sidebar-accent/50 focus:outline-none focus:ring-2 focus:ring-sidebar-ring focus:ring-offset-2 focus:ring-offset-sidebar"
        aria-expanded={expanded}
        aria-controls="investments-submenu"
        aria-label={`Investments menu, ${expanded ? 'expanded' : 'collapsed'}. Press Enter or Space to ${expanded ? 'collapse' : 'expand'}.`}
        aria-haspopup="true"
        aria-describedby={expanded ? "investments-submenu-instructions" : undefined}
      >
        <TrendingUp className="mr-2 h-4 w-4" aria-hidden="true" />
        {!collapsed && (
          <>
            <span>Investments</span>
            {expanded ? (
              <ChevronDown className="ml-auto h-4 w-4" aria-hidden="true" />
            ) : (
              <ChevronRight className="ml-auto h-4 w-4" aria-hidden="true" />
            )}
          </>
        )}
      </SidebarMenuButton>

      {!collapsed && expanded && (
        <>
          <div 
            id="investments-submenu-instructions" 
            className="sr-only"
            aria-live="polite"
          >
            Investment submenu opened. Use arrow keys to navigate, Enter to select, Escape to close.
          </div>
          <SidebarMenuSub 
            id="investments-submenu" 
            role="menu" 
            aria-label="Investment types"
            className="focus:outline-none"
          >
            {investmentSubItems.map((item, index) => {
              // Handle special case: both /investments and /investments/overview should activate Overview
              const isActive =
                location.pathname === item.url ||
                (item.slug === 'overview' && (location.pathname === '/investments' || location.pathname === '/investments/')) ||
                (item.slug === 'sacco' && location.pathname.startsWith('/investments/sacco'));

              // Insert SecuritiesMenuItem after Pension Plans (index 6, slug 'pension-plans')
              const isAfterPensionPlans = item.slug === 'reits';

              return (
                <React.Fragment key={item.slug}>
                  {isAfterPensionPlans && <SecuritiesMenuItem />}
                  <SidebarMenuSubItem role="none">
                    <SidebarMenuSubButton
                      asChild
                      isActive={isActive}
                      role="menuitem"
                      aria-current={isActive ? 'page' : undefined}
                      aria-posinset={index + 1}
                      aria-setsize={investmentSubItems.length}
                      className="focus:outline-none focus:ring-2 focus:ring-sidebar-ring focus:ring-offset-1 focus:ring-offset-sidebar focus:z-10"
                    >
                      <NavLink 
                        to={item.url}
                        ref={(el) => {
                          subMenuItemsRef.current[index] = el;
                        }}
                        onKeyDown={(e) => handleSubMenuItemKeyDown(index, e)}
                        aria-label={`${item.title} investment type`}
                        tabIndex={focusedIndex === index ? 0 : -1}
                      >
                        <span>{item.title}</span>
                      </NavLink>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </React.Fragment>
              );
            })}
          </SidebarMenuSub>
        </>
      )}
    </SidebarMenuItem>
  );
};
